import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { formatPlainTime, WrappedPlainTimeInterval } from '@airreading/common';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/tailwind-utils';

interface TimeOption {
  label: string;
  value: string;
}

export interface FormInputMobileTimeRangeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  start: Temporal.PlainTime;
  end: Temporal.PlainTime;
  onSave: (start: Temporal.PlainTime, end: Temporal.PlainTime) => void;
  step?: Temporal.Duration;
  min?: Temporal.PlainTime;
  max?: Temporal.PlainTime;
  'data-testid'?: string;
}

const DEFAULT_STEP = Temporal.Duration.from({ minutes: 15 });
const DEFAULT_MIN = Temporal.PlainTime.from('00:00:00');
const DEFAULT_MAX = Temporal.PlainTime.from('23:59:00');

const ITEM_HEIGHT_PX = 48;
const HALF_ITEM_PX = ITEM_HEIGHT_PX / 2;
const SCROLL_SETTLE_DELAY_MS = 100;
const HIDE_SCROLLBAR = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

const WHEEL_EFFECT_SCALE_PER_ROW = 0.12;
const WHEEL_EFFECT_MIN_SCALE = 0.72;
const WHEEL_EFFECT_OPACITY_PER_ROW = 0.26;
const WHEEL_EFFECT_MIN_OPACITY = 0.3;
const WHEEL_EFFECT_ROTATE_DEG_PER_ROW = 22;
const WHEEL_EFFECT_MAX_ROTATE_DEG = 66;

const clampIndex = (index: number, length: number) => Math.min(Math.max(index, 0), length - 1);

const wheelItemStyle = (distancePx: number): { transform: string; opacity: number } => {
  const rows = distancePx / ITEM_HEIGHT_PX;
  const absRows = Math.abs(rows);
  const scale = Math.max(WHEEL_EFFECT_MIN_SCALE, 1 - absRows * WHEEL_EFFECT_SCALE_PER_ROW);
  const opacity = Math.max(WHEEL_EFFECT_MIN_OPACITY, 1 - absRows * WHEEL_EFFECT_OPACITY_PER_ROW);
  const rotateX = Math.max(
    -WHEEL_EFFECT_MAX_ROTATE_DEG,
    Math.min(WHEEL_EFFECT_MAX_ROTATE_DEG, rows * WHEEL_EFFECT_ROTATE_DEG_PER_ROW),
  );
  return { transform: `rotateX(${rotateX}deg) scale(${scale})`, opacity };
};

interface TimeRangeColumnProps {
  options: TimeOption[];
  value: string;
  onSelect: (value: string) => void;
  'data-testid'?: string;
}

const TimeRangeColumn = ({ options, value, onSelect, 'data-testid': dataTestId }: TimeRangeColumnProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestAnimationFrameId = useRef<number | undefined>(undefined);
  // Setting scroll position programatically (not user gesture) should not
  // re-commit a selection
  const programmaticScroll = useRef(false);

  const initialIndex = options.findIndex((option) => option.value === value);
  // Live scroll offset, used to drive the per-item wheel transforms. Seeded to
  // the centered position so the first paint is already curved.
  const [scrollTop, setScrollTop] = useState(() => Math.max(0, initialIndex * ITEM_HEIGHT_PX));
  // Index currently under the centr line, tracked for highlighting.
  const [centeredIndex, setCenteredIndex] = useState(initialIndex);

  // Stable identity (only touches refs + a module constant) so it can be listed
  // as an effect dependency without re-running the effect every render.
  const centerOnIndex = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el || index < 0) return;
    const target = index * ITEM_HEIGHT_PX;
    if (Math.abs(el.scrollTop - target) > 1) {
      programmaticScroll.current = true;
      el.scrollTop = target;
    }
  }, []);

  // Re-center on selected value change
  useEffect(() => {
    const index = options.findIndex((option) => option.value === value);
    setCenteredIndex(index);
    centerOnIndex(index);
  }, [value, options, centerOnIndex]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const index = clampIndex(Math.round(el.scrollTop / ITEM_HEIGHT_PX), options.length);

    // One visual update per frame.
    if (requestAnimationFrameId.current == null) {
      requestAnimationFrameId.current = requestAnimationFrame(() => {
        requestAnimationFrameId.current = undefined;
        const node = scrollRef.current;
        if (!node) return;
        setScrollTop(node.scrollTop);
        setCenteredIndex(clampIndex(Math.round(node.scrollTop / ITEM_HEIGHT_PX), options.length));
      });
    }

    if (programmaticScroll.current) {
      programmaticScroll.current = false;
      return;
    }

    // Commit the centered option as the selection once scrolling settles.
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const option = options[index];
      if (option && option.value !== value) {
        onSelect(option.value);
      } else {
        // Snap exactly to center even when the selection did not change.
        centerOnIndex(index);
      }
    }, SCROLL_SETTLE_DELAY_MS);
  };

  useEffect(
    () => () => {
      clearTimeout(settleTimer.current);
      if (requestAnimationFrameId.current != null) cancelAnimationFrame(requestAnimationFrameId.current);
    },
    [],
  );

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={cn('flex-1 snap-y snap-mandatory overflow-y-auto [perspective:900px]', HIDE_SCROLLBAR)}
      data-testid={dataTestId}
    >
      <div style={{ height: `calc(50% - ${HALF_ITEM_PX}px)` }} className="shrink-0" aria-hidden />
      {options.map((option, index) => {
        const { transform, opacity } = wheelItemStyle(index * ITEM_HEIGHT_PX - scrollTop);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            style={{ height: ITEM_HEIGHT_PX, transform, opacity }}
            className={cn(
              'flex w-full snap-center items-center justify-center text-center text-lg tabular-nums',
              index === centeredIndex ? 'font-semibold text-air-primary' : 'text-gray-900',
            )}
            aria-pressed={option.value === value}
          >
            {option.label}
          </button>
        );
      })}
      <div style={{ height: `calc(50% - ${HALF_ITEM_PX}px)` }} className="shrink-0" aria-hidden />
    </div>
  );
};

export const FormInputMobileTimeRangeSheet = ({
  open,
  onOpenChange,
  start,
  end,
  onSave,
  step = DEFAULT_STEP,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  'data-testid': dataTestId,
}: FormInputMobileTimeRangeSheetProps) => {
  const [draftStart, setDraftStart] = useState<Temporal.PlainTime>(start);
  const [draftEnd, setDraftEnd] = useState<Temporal.PlainTime>(end);

  const options = useMemo<TimeOption[]>(() => {
    try {
      const opts = Array.from(new WrappedPlainTimeInterval(min, max).step(step)).map(
        (time): TimeOption => ({
          label: formatPlainTime(time, { hour12: true }),
          value: time.toString(),
        }),
      );
      const maxValue = max.toString();
      if (!opts.some((option) => option.value === maxValue)) {
        opts.push({ label: formatPlainTime(max, { hour12: true }), value: maxValue });
      }
      return opts;
    } catch {
      return [];
    }
  }, [step, min, max]);

  const handleSelectStart = (value: string) => {
    const nextStart = Temporal.PlainTime.from(value);
    // Disallow start later than end
    if (Temporal.PlainTime.compare(nextStart, draftEnd) > 0) {
      setDraftStart(nextStart);
      setDraftEnd(nextStart);
    } else {
      setDraftStart(nextStart);
    }
  };

  const handleSelectEnd = (value: string) => {
    const nextEnd = Temporal.PlainTime.from(value);
    // Disallow end before start
    if (Temporal.PlainTime.compare(nextEnd, draftStart) < 0) {
      setDraftStart(nextEnd);
      setDraftEnd(nextEnd);
    } else {
      setDraftEnd(nextEnd);
    }
  };

  const handleSave = () => {
    onSave(draftStart, draftEnd);
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setDraftStart(start);
      setDraftEnd(end);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="flex h-[55vh] flex-col rounded-t-2xl p-0" data-testid={dataTestId}>
        <SheetHeader className="px-4 pt-6 pb-2">
          <SheetTitle className="sr-only">Select availability window</SheetTitle>
          <SheetDescription className="sr-only">
            Scroll each column to set the start and end time, then press Save.
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 items-stretch px-4">
          <TimeRangeColumn
            options={options}
            value={draftStart.toString()}
            onSelect={handleSelectStart}
            data-testid={dataTestId ? `${dataTestId}-start-column` : undefined}
          />
          <div className="flex items-center px-2 text-lg text-gray-900">-</div>
          <TimeRangeColumn
            options={options}
            value={draftEnd.toString()}
            onSelect={handleSelectEnd}
            data-testid={dataTestId ? `${dataTestId}-end-column` : undefined}
          />
        </div>
        <div className="border-t p-4">
          <Button
            type="button"
            className="w-full bg-air-primary text-white hover:bg-air-primary/90"
            onClick={handleSave}
            data-testid={dataTestId ? `${dataTestId}-save` : undefined}
          >
            Save
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
